import React, { useEffect, useState, useCallback, useMemo } from "react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, extractErr, API_ROOT } from "@/lib/api";
import { Skeleton, EmptyState } from "@/components/EmptyState";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { BarChart3, Download, FileSpreadsheet, Filter, Truck, Calendar, Mail, Phone } from "lucide-react";

function fmtMoney(v) {
  const n = Number(v || 0);
  return `€${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString(); } catch { return s; }
}

export default function SupplierActivityReport() {
  const [suppliers, setSuppliers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState("");
  const [filters, setFilters] = useState({
    supplier_id: "",
    from: "",
    to: "",
    category: "",
    item_name: "",
  });
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get("/suppliers").then(({ data }) => setSuppliers(data.suppliers || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.supplier_id) params.supplier_id = filters.supplier_id;
      if (filters.from) params.from = new Date(filters.from).toISOString();
      if (filters.to) {
        // include the full day end
        const d = new Date(filters.to); d.setHours(23, 59, 59, 999);
        params.to = d.toISOString();
      }
      if (filters.category) params.category = filters.category;
      if (filters.item_name) params.item_name = filters.item_name;
      const { data } = await api.get("/reports/supplier-activity", { params });
      setData(data);
    } catch (e) {
      toast.error(extractErr(e, "Failed to load supplier report"));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const download = async (kind) => {
    setDownloading(kind);
    try {
      const token = localStorage.getItem("operon_token");
      const sp = new URLSearchParams();
      if (filters.supplier_id) sp.append("supplier_id", filters.supplier_id);
      if (filters.from) sp.append("from", new Date(filters.from).toISOString());
      if (filters.to) {
        const d = new Date(filters.to); d.setHours(23, 59, 59, 999);
        sp.append("to", d.toISOString());
      }
      if (filters.category) sp.append("category", filters.category);
      if (filters.item_name) sp.append("item_name", filters.item_name);
      const url = `${API_ROOT}/reports/supplier-activity/${kind}?${sp.toString()}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error(`Server returned ${resp.status}`);
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `supplier-activity.${kind === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      toast.error(`Failed to download ${kind.toUpperCase()}: ${e.message}`);
    } finally { setDownloading(""); }
  };

  const totals = data?.totals || {};
  const sList = data?.suppliers || [];
  const cats = ["Raw Materials", "Packaging Materials", "Finished Goods", "Consumables"];

  return (
    <AppShell
      title="Supplier Activity Report"
      subtitle={`${totals.suppliers || 0} supplier${(totals.suppliers || 0) === 1 ? "" : "s"} • ${totals.total_intakes || 0} intakes • Total value ${fmtMoney(totals.total_value)}`}
      headerActions={(
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" onClick={() => download("pdf")} disabled={!!downloading || (sList.length === 0)} data-testid="supplier-report-pdf"><Download className="w-4 h-4 mr-1.5" /> PDF</Button>
          <Button size="sm" variant="outline" onClick={() => download("excel")} disabled={!!downloading || (sList.length === 0)} data-testid="supplier-report-excel"><FileSpreadsheet className="w-4 h-4 mr-1.5" /> Excel</Button>
        </div>
      )}
    >
      <div className="rounded-2xl border border-border bg-card p-3 mb-4">
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground mb-2">
          <Filter className="w-3.5 h-3.5" /> Filters
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="space-y-1.5">
            <Label>Supplier</Label>
            <Select value={filters.supplier_id || "all"} onValueChange={(v) => setFilters((p) => ({ ...p, supplier_id: v === "all" ? "" : v }))}>
              <SelectTrigger data-testid="supplier-report-filter-supplier"><SelectValue placeholder="All suppliers" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All suppliers</SelectItem>
                {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>From</Label>
            <Input type="date" value={filters.from} onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value }))} data-testid="supplier-report-filter-from" />
          </div>
          <div className="space-y-1.5">
            <Label>To</Label>
            <Input type="date" value={filters.to} onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value }))} data-testid="supplier-report-filter-to" />
          </div>
          <div className="space-y-1.5">
            <Label>Category</Label>
            <Select value={filters.category || "all"} onValueChange={(v) => setFilters((p) => ({ ...p, category: v === "all" ? "" : v }))}>
              <SelectTrigger data-testid="supplier-report-filter-category"><SelectValue placeholder="Any category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any category</SelectItem>
                {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Item search</Label>
            <Input placeholder="Item name contains…" value={filters.item_name} onChange={(e) => setFilters((p) => ({ ...p, item_name: e.target.value }))} data-testid="supplier-report-filter-item" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32" />)}</div>
      ) : sList.length === 0 ? (
        <EmptyState icon={BarChart3} title="No supplier activity in range" description="Adjust filters or record goods intake against suppliers to see activity here." />
      ) : (
        <div className="space-y-3">
          {sList.map((s) => (
            <div key={s.supplier_id || s.name} className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`supplier-report-row-${s.supplier_id || "unassigned"}`}>
              <button
                type="button"
                onClick={() => setExpanded((p) => p === (s.supplier_id || s.name) ? null : (s.supplier_id || s.name))}
                className="w-full p-4 text-left hover:bg-accent/30"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Truck className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium">{s.name}</div>
                      {s.currency && s.currency !== "EUR" && <span className="rounded-full bg-primary/10 text-primary text-[10px] px-1.5 py-0.5 font-mono">{s.currency}</span>}
                      {(s.lead_time_label || s.lead_time_days) && (
                        <span className="text-[11px] text-primary flex items-center gap-1"><Calendar className="w-3 h-3" /> {s.lead_time_label || `${s.lead_time_days} days`}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-xs text-muted-foreground">
                      {s.contact_person && <span>{s.contact_person}</span>}
                      {s.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {s.email}</span>}
                      {s.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {s.phone}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-2xl font-semibold tabular-nums" data-testid={`supplier-report-value-${s.supplier_id || "unassigned"}`}>{fmtMoney(s.value)}</div>
                    <div className="text-xs text-muted-foreground">{s.total_intakes} intakes • {s.distinct_items} items</div>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <SmallStat label="Total qty" value={s.total_qty} />
                  <SmallStat label="First delivery" value={fmtDate(s.first_delivery)} />
                  <SmallStat label="Last delivery" value={fmtDate(s.last_delivery)} />
                  <SmallStat label="Avg freq" value={s.avg_delivery_freq_days != null ? `${s.avg_delivery_freq_days} d` : "—"} />
                </div>
              </button>
              {expanded === (s.supplier_id || s.name) && (
                <div className="border-t border-border" data-testid={`supplier-report-details-${s.supplier_id || "unassigned"}`}>
                  <div className="px-4 py-3">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Items received</div>
                    <div className="rounded-lg border border-border overflow-hidden">
                      <div className="grid grid-cols-12 px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/40">
                        <div className="col-span-2">SKU</div>
                        <div className="col-span-4">Item</div>
                        <div className="col-span-2">Category</div>
                        <div className="col-span-1 text-right">Qty</div>
                        <div className="col-span-1 text-right">Intakes</div>
                        <div className="col-span-2 text-right">Value</div>
                      </div>
                      <div className="max-h-64 overflow-y-auto divide-y divide-border">
                        {s.items.map((it) => (
                          <div key={it.item_id} className="grid grid-cols-12 px-3 py-2 text-sm items-center">
                            <div className="col-span-2 font-mono text-xs">{it.sku}</div>
                            <div className="col-span-4 truncate">{it.name}</div>
                            <div className="col-span-2 text-xs text-muted-foreground">{it.category}</div>
                            <div className="col-span-1 text-right tabular-nums">{Number(it.qty).toLocaleString()}</div>
                            <div className="col-span-1 text-right tabular-nums text-muted-foreground">{it.intakes}</div>
                            <div className="col-span-2 text-right tabular-nums font-medium">{fmtMoney(it.value)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="px-4 pb-4">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Monthly intake</div>
                    <div className="flex items-end gap-1 h-24">
                      <MonthlyChart months={s.monthly_history || []} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}

function SmallStat({ label, value }) {
  return (
    <div>
      <div className="text-muted-foreground">{label}</div>
      <div className="font-medium tabular-nums">{value}</div>
    </div>
  );
}

function MonthlyChart({ months }) {
  if (!months || months.length === 0) {
    return <div className="text-xs text-muted-foreground">No monthly data.</div>;
  }
  const max = Math.max(...months.map((m) => Number(m.qty || 0)), 1);
  return (
    <div className="flex items-end gap-1 w-full">
      {months.map((m) => {
        const h = Math.max(4, (Number(m.qty || 0) / max) * 80);
        return (
          <div key={m.month} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full bg-primary/70 rounded-t" style={{ height: `${h}px` }} title={`${m.month}: ${m.qty} qty / ${m.intakes} intakes`} />
            <div className="text-[10px] text-muted-foreground">{m.month.slice(5)}</div>
          </div>
        );
      })}
    </div>
  );
}
